/**
 * Images Configuration
 * Centralized image URLs for all public pages and components
 * Theme-aware: Supports light and dark mode variations
 *
 * Bucket-backed assets resolve at read time via getPublicFileUrl() so localhost
 * always picks up VITE_STORAGE_STAGING_PUBLIC_BASE_URL.
 */

import { getPublicFileUrl } from '@/utils/storage-public-url.util'

export type ThemeImageConfig =
  | { light: string; dark?: string }
  | { storageKey: string; dark?: string }

function resolveThemeImageUrls(config: ThemeImageConfig): { light: string; dark?: string } {
  if ('storageKey' in config) {
    const url = getPublicFileUrl(config.storageKey)
    return { light: url, dark: config.dark ?? url }
  }
  return { light: config.light, dark: config.dark }
}

/**
 * Get theme-aware image URL
 * Returns light or dark version based on current theme
 */
export function getThemeImage(
  lightUrl: string,
  darkUrl?: string,
  isDarkMode: boolean = false
): string {
  return isDarkMode && darkUrl ? darkUrl : lightUrl
}

/**
 * Public Page Images
 * Images used across public marketing pages
 */
export const publicImages = {
  // Auth & Onboarding
  auth: {
    workspace: {
      light: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1000&h=1200&fit=crop&q=80',
      dark: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1000&h=1200&fit=crop&q=80',
    },
    success: {
      light: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=800&fit=crop&q=80',
      dark: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=800&fit=crop&q=80',
    },
  },

  // Registration
  registration: {
    account: {
      light: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&h=800&fit=crop&q=80',
      dark: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&h=800&fit=crop&q=80',
    },
    organization: {
      light: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=800&fit=crop&q=80',
      dark: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=800&fit=crop&q=80',
    },
  },

  // Onboarding Wizard Backgrounds
  onboarding: {
    step1: {
      light: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=2000&h=1200&fit=crop&q=80',
      dark: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=2000&h=1200&fit=crop&q=80',
    },
    step2: {
      light: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=2000&h=1200&fit=crop&q=80',
      dark: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=2000&h=1200&fit=crop&q=80',
    },
    step3: {
      light: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=2000&h=1200&fit=crop&q=80',
      dark: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=2000&h=1200&fit=crop&q=80',
    },
    step4: {
      light: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=2000&h=1200&fit=crop&q=80',
      dark: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=2000&h=1200&fit=crop&q=80',
    },
    step5: {
      light: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=2000&h=1200&fit=crop&q=80',
      dark: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=2000&h=1200&fit=crop&q=80',
    },
  },
} as const

/**
 * Helper to get image URL with theme detection (non-React)
 * For use in non-React contexts or server-side
 */
export function getImageUrl(imageConfig: ThemeImageConfig): string {
  const resolved = resolveThemeImageUrls(imageConfig)
  if (typeof window === 'undefined') {
    return resolved.light
  }

  const isDarkMode = document.documentElement.classList.contains('dark')
  return getThemeImage(resolved.light, resolved.dark, isDarkMode)
}
